import { uuidv7 } from "@ets/shared";
import { COOKIE_NAMES, TTL } from "@ets/types";
import type { CookieOptions } from "./cookie.js";
import { getCookie, setCookie } from "./cookie.js";
import { getPersistedJson, setPersistedJson } from "./storage.js";

const SESSION_COOKIE_OPTIONS: CookieOptions = {
  maxAge: TTL.SESSION_SECONDS,
  sameSite: "Lax",
  secure: true,
};

interface StoredSession {
  id: string;
  expiresAt: number;
  previousSessionId?: string;
}

export interface SessionIdResult {
  sessionId: string;
  isNewSession: boolean;
  previousSessionId: string | undefined;
}

function isStoredSession(value: unknown): value is StoredSession {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as StoredSession).id === "string" &&
    typeof (value as StoredSession).expiresAt === "number"
  );
}

/**
 * Resolve the client session ID:
 * 1. Read from cookie ets_csid — if valid, refresh TTL (sliding window)
 * 2. Fallback to localStorage with manual TTL — if valid, re-persist to cookie
 * 3. Generate new UUID v7, track previousSessionId when session expires
 */
export function resolveSessionId(): SessionIdResult {
  const now = Date.now();
  const cookieName = COOKIE_NAMES.CLIENT_SESSION_ID;

  // 1. Try cookie
  const cookieValue = getCookie(cookieName);
  if (cookieValue) {
    // Refresh cookie TTL (sliding window)
    setCookie(cookieName, cookieValue, SESSION_COOKIE_OPTIONS);
    // Also refresh localStorage TTL, preserving previousSessionId
    const stored = getPersistedJson<StoredSession>(cookieName, isStoredSession);
    setPersistedJson<StoredSession>(cookieName, {
      id: cookieValue,
      expiresAt: now + TTL.SESSION_SECONDS * 1000,
      previousSessionId: stored?.previousSessionId,
    });
    return {
      sessionId: cookieValue,
      isNewSession: false,
      // Preserve cross-session correlation: if a previousSessionId is
      // persisted in localStorage it must flow back to callers so events
      // emitted early in the session can carry the correlation reliably.
      previousSessionId: stored?.previousSessionId,
    };
  }

  // 2. Fallback: check localStorage with manual TTL
  const stored = getPersistedJson<StoredSession>(cookieName, isStoredSession);
  if (stored && stored.expiresAt > now) {
    // Valid session in localStorage but cookie was cleared — re-persist
    setCookie(cookieName, stored.id, SESSION_COOKIE_OPTIONS);
    // Refresh localStorage TTL
    setPersistedJson<StoredSession>(cookieName, {
      id: stored.id,
      expiresAt: now + TTL.SESSION_SECONDS * 1000,
      previousSessionId: stored.previousSessionId,
    });
    return {
      sessionId: stored.id,
      isNewSession: false,
      previousSessionId: stored.previousSessionId,
    };
  }

  // 3. Session expired or missing — create new one
  const previousSessionId = stored?.id ?? stored?.previousSessionId;
  const newId = uuidv7();

  setCookie(cookieName, newId, SESSION_COOKIE_OPTIONS);
  setPersistedJson<StoredSession>(cookieName, {
    id: newId,
    expiresAt: now + TTL.SESSION_SECONDS * 1000,
    previousSessionId,
  });

  return {
    sessionId: newId,
    isNewSession: true,
    previousSessionId,
  };
}
